import {useCallback} from 'react';
import {DeepPartial} from 'ts-essentials';
import {DEFAULT_QUERY_CONFIG, useQueryClient} from '../react-query';
import type {
  InferConfigType,
  InferFinalResponseType,
  InferResponseType,
  MakeAllKeysRequired,
} from '../types';
import {generateCacheKey} from '../useRequest';
import {GrpcConfig, useGrpcClientConfig} from './GrpcClientContext';
import {isUnimplementedError} from './code';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';
import {CacheKeyConfig, createCacheKey} from './createCacheKey';
import {
  GrpcMutationConfigWithSection,
  GrpcRequestServiceMethod,
  useGenericGrpcRequestFn,
} from './useGenericGrpcRequest';

export interface GenericTadaridaConfig {
  config?: DeepPartial<GrpcConfig>;
}

/**
 * This hook is used to create a callback that can be used to retrieve data
 * unlike the useGrpcRequestMutation hook, which is used to mutate data
 * @param lazyAsyncService
 * @param grpcQueryConfig
 * @returns
 */
export function useGrpcRequestCallback<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<Awaited<ReturnType<ServiceMethod>>>[1],
      keyof Partial<GenericTadaridaConfig>
    >
  >,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  >,
  MethodName extends ServiceMethod['method'],
  DefaultConfigType extends GrpcMutationConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(lazyAsyncService: ServiceMethod, grpcQueryConfig?: GrpcConfigType) {
  const genericConfig = useGrpcClientConfig();

  const {transformCacheKey, ...queryConfig} = grpcQueryConfig ?? {};

  const fetch = useGenericGrpcRequestFn(
    lazyAsyncService,
    /*
      This is an empty object because we dont know the
      params on render yet, because of that we pass
      them while calling fetch
    */
    {} as ParamsType,
    {config: genericConfig},
  );

  const queryClient = useQueryClient();

  return useCallback(
    (params?: ParamsType): Promise<TData> => {
      if (
        grpcQueryConfig?.resolveMockData !== 'never' &&
        grpcQueryConfig?.mockData
      ) {
        return new Promise<ResponseType>((resolve, reject) => {
          if (grpcQueryConfig?.resolveMockData === 'always') {
            grpcQueryConfig?.mockData?.(params).then(resolve);
          } else {
            fetch()
              .then(resolve)
              .catch(async error => {
                if (
                  isUnimplementedError(error) &&
                  grpcQueryConfig.resolveMockData === 'not-implemented'
                ) {
                  return grpcQueryConfig?.mockData?.(params).then(resolve);
                }
                return reject(error);
              });
          }
        });
      }

      const uniqueIdentifier = lazyAsyncService.method;
      const cacheKeyConfig = {
        uniqueIdentifier,
        params,
      } as CacheKeyConfig;
      const key = createCacheKey(
        transformCacheKey ? transformCacheKey(cacheKeyConfig) : cacheKeyConfig,
      );

      return queryClient.fetchQuery(
        generateCacheKey(key),
        () =>
          fetch(
            convertAllNullPropertiesToUndefined<ParamsType | undefined>(params),
          ),
        {
          ...DEFAULT_QUERY_CONFIG,
          ...queryConfig,
        },
      );
    },
    [
      queryClient,
      fetch,
      queryConfig,
      transformCacheKey,
      lazyAsyncService,
      grpcQueryConfig?.mockData,
      grpcQueryConfig?.resolveMockData,
    ],
  );
}

/**
 * This is a raw version of the useGrpcRequestCallback hook which does not use react-query and CAN BE called on the server
 */
export function useRawGrpcRequestCallback<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<Awaited<ReturnType<ServiceMethod>>>[1],
      keyof Partial<GenericTadaridaConfig>
    >
  >,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  >,
  MethodName extends ServiceMethod['method'],
  DefaultConfigType extends GrpcMutationConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(lazyAsyncService: ServiceMethod, grpcQueryConfig?: GrpcConfigType) {
  const genericConfig = useGrpcClientConfig();

  const {transformCacheKey, ...queryConfig} = grpcQueryConfig ?? {};

  const fetch = useGenericGrpcRequestFn(
    lazyAsyncService,
    /*
      This is an empty object because we dont know the
      params on render yet, because of that we pass
      them while calling fetch
    */
    {} as ParamsType,
    {config: genericConfig},
  );

  return useCallback(
    (params?: ParamsType): Promise<TData> => {
      return fetch(
        convertAllNullPropertiesToUndefined<ParamsType | undefined>(params),
      );
    },
    [
      fetch,
      queryConfig,
      transformCacheKey,
      lazyAsyncService,
      grpcQueryConfig?.mockData,
      grpcQueryConfig?.resolveMockData,
    ],
  );
}
